import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAllUsers from '../hooks/useAllUsers';
import useUserByPhone from '../hooks/useUserByPhone';

const AllUsers = () => {
    const navigate = useNavigate();

    const [phone, setPhone] = useState('');
    const [searchPhone, setSearchPhone] = useState(''); 
    
    // Fetch all users for default view
    const { 
        users: allUsers, 
        isLoading: isLoadingAll, 
        error: errorAll, 
        message: messageAll, 
        loadMore, 
        hasMore 
    } = useAllUsers(1, 20);
    
    // Search specific user by phone
    const { 
        user: searchedUser, 
        isLoading: isLoadingSearch, 
        error: errorSearch, 
        message: messageSearch 
    } = useUserByPhone(searchPhone);

    // Determine what to display
    const isSearching = searchPhone.length > 0;
    const users = isSearching ? (searchedUser ? [searchedUser] : []) : allUsers;
    const isLoading = isSearching ? isLoadingSearch : isLoadingAll;
    const error = isSearching ? errorSearch : errorAll;
    const message = isSearching ? messageSearch : messageAll;

    const handleSearch = () => {
        setSearchPhone(phone.trim());
    };

    const handleClear = () => {
        setPhone('');
        setSearchPhone('');
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleRowClick = (userId) => {
        navigate(`/payment-transactions/${userId}`);
    };

    return (
        <div className="p-4">
            <h2 className="text-2xl font-semibold mb-4">All Users</h2>

            {/* Search Box */}
            <div className="mb-4 flex gap-2">
                <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Search by phone..."
                    className="flex-1 min-w-0 border px-3 py-2 rounded"
                />
                <button
                    onClick={handleSearch}
                    disabled={isLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                >
                    Search
                </button>
                <button
                    onClick={handleClear}
                    className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
                >
                    Clear
                </button>
            </div>

            {/* Search Info */}
            {isSearching && (
                <p className="text-sm text-gray-600 mb-2">
                    {isLoadingSearch ? 'Searching...' : `Search results for "${searchPhone}"`}
                </p>
            )}

            {/* Error & Message */}
            {error && <p className="text-red-500 mb-2">{error}</p>}
            {message && !error && <p className="text-green-600 mb-2">{message}</p>}

            {/* Loading State */}
            {isLoading && users.length === 0 && (
                <p className="text-gray-500 text-center py-8">Loading...</p>
            )}

            {/* Users Table */}
            {!isLoading && users.length > 0 ? (
                <table className="w-full border border-gray-300">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="border px-3 py-2">ID</th>
                            <th className="border px-3 py-2">Name</th>
                            <th className="border px-3 py-2">Phone</th>
                            <th className="border px-3 py-2">Email</th>
                            <th className="border px-3 py-2">Created At</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(user => (
                            <tr
                                key={user.id}
                                className="hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleRowClick(user.id)}
                            >
                                <td className="border px-3 py-2">{user.id}</td>
                                <td className="border px-3 py-2">{user.name}</td>
                                <td className="border px-3 py-2">{user.phone}</td>
                                <td className="border px-3 py-2">{user.email || '-'}</td>
                                <td className="border px-3 py-2">
                                    {new Date(user.created_at).toLocaleString()}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : !isLoading && (
                <p className="text-gray-500 text-center py-8">
                    {isSearching ? 'No user found with this phone number.' : 'No users available.'}
                </p>
            )}

            {/* Load More Button - only show when NOT searching */}
            {!isSearching && hasMore && (
                <div className="mt-4 text-center">
                    <button
                        onClick={loadMore}
                        disabled={isLoadingAll}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                    >
                        {isLoadingAll ? 'Loading...' : 'Load More Users'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default AllUsers;